import React, { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useValidationErrors } from '@/Hooks/useValidationErrors';
import BaseTextInput from './Form/BaseTextInput';
import { Divider } from "primereact/divider";
import BaseDropdown from './Form/BaseDropdown';
import { Button } from "primereact/button";
import { User, UserRoleEnum } from "@/Stub/V1";
import { useGetClients } from "@/Service/Api/V1/ApiHooks/Clients/useGetClients";
import { useGetTeams } from "@/Service/Api/V1/ApiHooks/Teams/useGetTeams";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import { snakeToNormalCase } from "@/Helpers/general";


const UserFormSchema = z.object({
    client_id: z.number(),
    team_id: z.number(),
    name: z.string({
        required_error: 'Name is required'
    }),
    role: z.nativeEnum(UserRoleEnum),
    email: z.string({
        required_error: 'Email is required'
    }).email(),
    password: z.string()
        .optional()
        .refine(
            (val) => !val || val.length === 0 || val.length >= 8,
            {
                message: 'Password must contain at least 8 characters (if provided).',
            }
        ),
    confirm_password: z.string()
        .optional()
        .refine(
            (val) => !val || val.length === 0 || val.length >= 8,
            {
                message: 'Password must contain at least 8 characters (if provided).',
            }
        ),
}).superRefine((data, ctx) => {
    if (data.password && !(/\d/.test(data.password))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Password must contain at least one number.',
            path: ['password']
        });
    }
    if (data.password && data.password.toLowerCase() === data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Password must contain at least one uppercase and one lowercase letter.',
            path: ['password']
        });
    }

    if (data.password && data.confirm_password !== data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Passwords do not match',
            path: ['confirm_password'],
        });
    }
});

export type UserFormData = z.infer<typeof UserFormSchema>;

const roleOptions = Object.entries(UserRoleEnum).map(([key, value]) => ({
    key,
    value,
    label: snakeToNormalCase(value.replace('ng_', '')),
}));

export type UserFormFormProps = {
    className?: string
    onSubmit?: (data: UserFormData) => void
    onSubmitButtonLabel?: string
    onSubmitButtonDisabled?: boolean
    isProcessing?: boolean
    initFormData?: User & { is_non_sso_user?: boolean }
    errorBag?: Error[]
};

const UserForm: React.FC<UserFormFormProps> = ({
    className,
    onSubmit,
    isProcessing,
    initFormData,
    onSubmitButtonLabel,
    onSubmitButtonDisabled = false,
    errorBag
}: UserFormFormProps) => {
    UserFormSchema.superRefine((data, ctx) => {
        if (!initFormData) {
            if (!data.password) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Password is required',
                    path: ['password']
                });
            }

        } else {
            if (data.password && !(/\d/.test(data.password))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Password must contain at least one number.',
                    path: ['password']
                });
            }
        }
    });

    const formInitialValues = useMemo(() => {
        if(initFormData){
            return {
                ...initFormData,
                name: initFormData.name,
                client_id: initFormData?.client?.id,
                team_id: initFormData?.team?.id
            };
        }

        return {
            client_id: undefined,
            team_id: undefined,
            name: '',
            role: UserRoleEnum.NgUser,
            email: '',
            password: '',
            confirm_password: ''
        };
    }, [initFormData]);

    const {
        control,
        setError,
        reset,
        setValue,
        watch,
        formState: { errors },
        handleSubmit,
    } = useForm<UserFormData>({
        resolver: zodResolver(UserFormSchema),
        defaultValues: { ...formInitialValues }
    });

    const onFormSubmit = (data: UserFormData) => {
        if (onSubmit) {
            onSubmit(data);
        }
    };

    const { data, isLoading: isLoadingClients } = useGetClients({
        requestParams: {
            page: 1,
            per_page: 1000
        }
    });

    const watchClient = watch('client_id');

    useEffect(() => {
        if (watchClient) {
            setValue('team_id', undefined);
        }
    }, [watchClient, setValue]);

    const { data: dataTeams, isLoading: isLoadingTeams } = useGetTeams({
        requestParams: {
            page: 1,
            per_page: 1000,
            client_id: watchClient
        }
    });

    const organizationOptions = useMemo(() => {
        const clients = data?.data || [];
        return [
            ...clients.map(client => ({
                key: client.id,
                label: client.name,
                value: client.id
            }))
        ];
    }, [data]);

    const teamOptions = useMemo(() => {
        const teams = dataTeams?.data || [];
        return [
            ...teams.map(team => ({
                key: team.id,
                label: team.name,
                value: team.id
            }))
        ];
    }, [dataTeams]);


    useEffect(() => {
        if(initFormData) {
            reset({
                ...initFormData,
                name: initFormData.name,
                client_id: initFormData?.client?.id,
                team_id: initFormData?.team?.id
            });
        } else {
            reset({
                client_id: organizationOptions.length > 0 ? organizationOptions[0].value : undefined,
                team_id: undefined,
                name: '',
                role: UserRoleEnum.NgUser,
                email: '',
                password: '',
                confirm_password: ''
            });
        }
    }, [initFormData, organizationOptions, reset]);

    useValidationErrors(setError, errorBag);

    if(isLoadingClients || isLoadingTeams) {
        return <ContentLoadingTemplate />;
    }

    return (
        <div className={clsx(className, 'user-form')}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div className="grid">
                    <h4 className={'col-12 my-0 font-bold'}>Personal info</h4>
                    <BaseTextInput
                        className={"col-6 rounded-md"}
                        pt={{ inputText: { className: 'w-full' } }}
                        control={control}
                        name={"name"}
                        label={'Name'}
                        errorMessages={errors.name?.message}
                    />
                    <BaseTextInput
                        className={"col-6 rounded-md"}
                        pt={{ inputText: { className: 'w-full' } }}
                        control={control}
                        required
                        readOnly={!!initFormData}
                        type={"email"}
                        name={"email"}
                        label={'Email'}
                        errorMessages={errors.email?.message}
                    />
                    {
                        (!initFormData ||  initFormData.is_non_sso_user) ?
                            <>
                                <BaseTextInput
                                    className={"col-6 rounded-md"}
                                    pt={{ inputText: { className: 'w-full' } }}
                                    control={control}
                                    tooltip={initFormData ? 'Leave it blank if don\'t want to change user\'s password!' : undefined}
                                    tooltipOptions={{ position: 'top' }}
                                    type={'password'}
                                    required={!initFormData}
                                    name={"password"}
                                    label={'Password'}
                                    errorMessages={errors.password?.message}
                                />
                                <BaseTextInput
                                    className={"col-6 rounded-md"}
                                    pt={{ inputText: { className: 'w-full' } }}
                                    control={control}
                                    tooltip={initFormData ? 'Leave it blank if don\'t want to change user\'s password!' : undefined}
                                    tooltipOptions={{ position: 'top' }}
                                    type={'password'}
                                    required={!initFormData}
                                    name={"confirm_password"}
                                    label={'Confirm Password'}
                                    errorMessages={errors.confirm_password?.message}
                                />
                            </>

                            : undefined
                    }

                    <Divider className={'col-12'}/>
                    <h4 className={'col-12 my-0 font-bold'}>General</h4>
                    <BaseDropdown
                        className={"col-6 rounded-md"}
                        pt={{ dropdown: { className: 'w-full' } }}
                        control={control}
                        options={roleOptions}
                        required
                        name={"role"}
                        label={'Role'}
                        errorMessages={errors.role?.message}
                    />
                    <BaseDropdown
                        required
                        className={"col-6 rounded-md"}
                        name={"client_id"}
                        pt={{ dropdown: { className: 'w-full' } }}
                        label="Organization"
                        control={control}
                        errorMessages={errors.client_id?.message}
                        options={organizationOptions}
                    />
                    <BaseDropdown
                        required
                        className={"col-6 rounded-md"}
                        name={"team_id"}
                        pt={{ dropdown: { className: 'w-full' } }}
                        label="Team"
                        control={control}
                        errorMessages={errors.team_id?.message}
                        options={teamOptions}
                    />
                </div>
                <div className="flex mt-4">
                    <Button
                        className={"ml-auto"}
                        label={onSubmitButtonLabel}
                        disabled={onSubmitButtonDisabled}
                        type={"submit"}
                        severity={"success"}
                        loading={isProcessing}
                    />
                </div>
            </form>
        </div>
    );
};

export default UserForm;
